"use client";

import { useCallback, useMemo } from "react";
import {
  selectCompactPreviewModel,
  selectGanttContainerHeight,
  type GanttCompactPreviewModel,
} from "./gantt-render-selectors";
import type { GanttLayout, ParsedGantt } from "./gantt-types";
import {
  useGanttHoverController,
  type UseGanttHoverControllerResult,
} from "./useGanttHoverController";
import {
  useGanttLayoutController,
  type UseGanttLayoutControllerResult,
} from "./useGanttLayoutController";
import {
  useGanttTimelineViewport,
  type UseGanttTimelineViewportResult,
} from "./useGanttTimelineViewport";
import {
  useGanttZoomControls,
  type UseGanttZoomControlsResult,
} from "./useGanttZoomControls";

const GANTT_VALUE = "gantt";

const GANTT_ID_UNSAFE_CHARS_REGEX = /[^a-zA-Z0-9_-]/g;
const GANTT_ID_EDGE_DASHES_REGEX = /^-+|-+$/g;
const GANTT_ID_HASH_OFFSET_BASIS = 2166136261;
const GANTT_ID_HASH_PRIME = 16777619;

export interface UseCmarkdownganttControllerParams {
  content: string;
}

export interface CmarkdownganttController {
  ganttIdPrefix: string;
  parsed: ParsedGantt;
  layout: GanttLayout;
  containerHeight: number;
  compactPreviewModel: GanttCompactPreviewModel | null;
  timelineViewport: UseGanttTimelineViewportResult;
  hover: UseGanttHoverControllerResult;
  zoom: UseGanttZoomControlsResult;
  isEntranceComplete: boolean;
  onToggleSection: (sectionIndex: number) => void;
}

function sanitizeGanttIdSegment(value: string): string {
  const sanitized = value
    .replace(GANTT_ID_UNSAFE_CHARS_REGEX, "-")
    .replace(GANTT_ID_EDGE_DASHES_REGEX, "");

  return sanitized || GANTT_VALUE;
}

function hashGanttIdSource(value: string): string {
  let hash = GANTT_ID_HASH_OFFSET_BASIS;
  for (let characterIndex = 0; characterIndex < value.length; characterIndex += 1) {
    hash ^= value.charCodeAt(characterIndex);
    hash = Math.imul(hash, GANTT_ID_HASH_PRIME);
  }

  return (hash >>> 0).toString(36);
}

function createGanttIdPrefix(input: {
  content: string;
  parsed: ParsedGantt | null;
}): string {
  const semanticSource = input.parsed
    ? [
        input.parsed.title ?? GANTT_VALUE,
        ...input.parsed.sections.map((section) => section.label),
        ...input.parsed.tasks.map((task) => `${task.label}-${task.status}`),
      ].join("-")
    : input.content;
  const semanticSegment = sanitizeGanttIdSegment(semanticSource).slice(0, 72);

  return `gantt-${semanticSegment}-${hashGanttIdSource(input.content)}`;
}

export function useCmarkdownganttController({
  content,
}: UseCmarkdownganttControllerParams): CmarkdownganttController | null {
  const timelineViewport = useGanttTimelineViewport();
  const layoutController = useGanttLayoutController({
    content,
    timelineViewportWidth: timelineViewport.timelineViewportState.width,
  });
  const parsed = layoutController.parsed;
  const ganttIdPrefix = useMemo(
    () => createGanttIdPrefix({ content, parsed }),
    [content, parsed],
  );
  const setCollapsedSections = layoutController.setCollapsedSections;
  const hover = useGanttHoverController({
    parsed,
    layout: layoutController.layout,
    timelineViewportState: timelineViewport.timelineViewportState,
  });
  const zoom = useGanttZoomControls({
    fitPxPerDay: layoutController.fitPxPerDay,
    effectivePxPerDay: layoutController.effectivePxPerDay,
    pxPerDayOverride: layoutController.pxPerDayOverride,
    setPxPerDayOverride: layoutController.setPxPerDayOverride,
    layout: layoutController.layout,
    timelineScrollRef: timelineViewport.timelineScrollRef,
  });

  const onToggleSection = useCallback((sectionIndex: number): void => {
    setCollapsedSections((previous) => {
      const next = new Set(previous);
      if (next.has(sectionIndex)) {
        next.delete(sectionIndex);
      } else {
        next.add(sectionIndex);
      }
      return next;
    });
  }, [setCollapsedSections]);

  const compactPreviewModel = useMemo(() => {
    return selectCompactPreviewModel({
      parsed,
    });
  }, [parsed]);

  const controller = selectResolvedController({
    ganttIdPrefix,
    layoutController,
    compactPreviewModel,
    timelineViewport,
    hover,
    zoom,
    onToggleSection,
  });

  return controller;
}

interface SelectResolvedControllerParams {
  ganttIdPrefix: string;
  layoutController: UseGanttLayoutControllerResult;
  compactPreviewModel: GanttCompactPreviewModel | null;
  timelineViewport: UseGanttTimelineViewportResult;
  hover: UseGanttHoverControllerResult;
  zoom: UseGanttZoomControlsResult;
  onToggleSection: (sectionIndex: number) => void;
}

function selectResolvedController({
  ganttIdPrefix,
  layoutController,
  compactPreviewModel,
  timelineViewport,
  hover,
  zoom,
  onToggleSection,
}: SelectResolvedControllerParams): CmarkdownganttController | null {
  if (
    !layoutController.parsed ||
    !layoutController.layout ||
    layoutController.layout.taskLayouts.length < 1
  ) {
    return null;
  }

  return {
    ganttIdPrefix,
    parsed: layoutController.parsed,
    layout: layoutController.layout,
    containerHeight: selectGanttContainerHeight({
      chartHeight: layoutController.layout.chartHeight,
    }),
    compactPreviewModel,
    timelineViewport,
    hover,
    zoom,
    isEntranceComplete: layoutController.isEntranceComplete,
    onToggleSection,
  };
}
